import humanizeString from 'humanize-string';
import { ReactNode } from 'react';

import { DateRenderer, RecordType } from '../shared';
import { getRenderer } from './recordHeaders';
import { hasValue } from './RecordOverview';

interface InfoRenderersType {
  [name: string]: (value: ReactNode, record: RecordType) => ReactNode;
}

// These values are treated as empty.
const emptyValues = [null, undefined, 'NotImplemented'];

const recordHasProperty = (name: string, record: RecordType) =>
  record.hasOwnProperty(name) && !emptyValues.includes(record[name]);

/**
 * For each of the passed `names` display a list of existing values. If an
 * override property name is defined, then it should take sole precedence for
 * rendering the value. If it or its value doesn't exist, then fallback to
 * render a list of existing values in `names`.
 */
const fallbackRenderer = (
  value: ReactNode,
  record: RecordType,
  fieldNames: string[],
  overrideName?: string,
  skipIpIfHostname = true
) => {
  let names: string[] = [...fieldNames];
  if (overrideName && recordHasProperty(overrideName, record)) {
    names = [overrideName];
  }

  // If the list of names already includes an existing hostname value then skip
  // the IP address. It is not necessary to show both a hostname and IP address
  // with this renderer.
  const includesHostname =
    skipIpIfHostname &&
    names.some((n) => n.endsWith('_hostname') && recordHasProperty(n, record));

  const matchingNames = names.filter((name) => {
    const hasMatch = recordHasProperty(name, record);
    const skip = hasMatch && includesHostname && name.endsWith('_ip');

    return skip ? false : hasMatch;
  });

  // If there is more than one value, then include labels.
  const count = matchingNames.length;
  const template = matchingNames
    .map((n) => `${count > 1 ? `${humanizeString(n)}: ` : ''}{${n}}`)
    .join(', ');

  return getRenderer(template)(value, record);
};

const fallbackFields = {
  Audit: [
    'action',
    'application',
    'device_hostname',
    'device_ip',
    'user_username',
  ],
  AuditChange: [
    'changeType',
    'changeTarget',
    'application',
    'device_hostname',
    'device_ip',
    'user_username',
  ],
  AuditResourceAccess: [
    'resource',
    'application',
    'device_hostname',
    'device_ip',
    'user_username',
  ],
  AuthenticationPrivilegeEscalation: [
    'fromUser_username',
    'srcDevice_ip',
    'srcDevice_hostname',
    'device_ip',
    'device_hostname',
    'user_username',
  ],
  Endpoint: ['device_ip', 'device_hostname', 'user_username'],
  EndpointModuleLoad: [
    'device_ip',
    'device_hostname',
    'user_username',
    'file_path',
  ],
  EndpointProcess: [
    'device_ip',
    'device_hostname',
    'user_username',
    'commandLine',
    'threat_identifier',
  ],
  Network: [
    'srcDevice_ip',
    'srcDevice_hostname',
    'dstDevice_ip',
    'dstDevice_hostname',
  ],
  NetworkFlow: [
    'srcDevice_ip',
    'srcDevice_hostname',
    'dstDevice_ip',
    'dstDevice_hostname',
    'ipProtocol',
  ],
  Notification: ['device_ip', 'device_hostname', 'user_username'],
  NotificationVulnerability: [
    'device_ip',
    'device_hostname',
    'user_username',
    'vuln_name',
  ],
};

const networkRenderer = (value: ReactNode, record: RecordType) => {
  // Method & URL
  const prefix: any[] = [];
  if (record.http_method) {
    prefix.push('{http_method}');
  }
  if (record.http_url) {
    prefix.push('{http_url}');
  }

  // Status code & content length
  const postfix: any[] = [];
  if (record.http_response_statusCode) {
    postfix.push('{http_response_statusCode}');
  }
  if (record.http_response_contentLength) {
    postfix.push('{http_response_contentLength}');
  }

  return prefix.length && postfix.length
    ? getRenderer(`${prefix.join(' ')} ${postfix.join('; ')}`)(value, record)
    : 'None';
};

export const infoRenderers: InfoRenderersType = {
  Audit: (value, record) =>
    fallbackRenderer(value, record, fallbackFields.Audit, 'description'),
  AuditChange: (value, record) =>
    fallbackRenderer(value, record, fallbackFields.AuditChange, 'description'),
  AuditResourceAccess: (value, record) =>
    fallbackRenderer(
      value,
      record,
      fallbackFields.AuditResourceAccess,
      'description'
    ),
  Authentication: (value, record) => {
    // Status message
    let status = '';
    const { success } = record;
    if (success === true || success === 'True') {
      status = 'successful ';
    } else if (success === false || success === 'False') {
      status = 'unsuccessful ';
    }

    // IP address
    const ipAddress = hasValue(record.srcDevice_ip)
      ? '{srcDevice_ip}'
      : hasValue(record.device_ip)
        ? '{device_ip}'
        : 'unknown IP';

    // Country code
    const countryCode = hasValue(record.srcDevice_ip_countryCode)
      ? '({srcDevice_ip_countryCode}) '
      : '';

    // User
    const user = hasValue(record.user_username)
      ? '{user_username}'
      : 'unknown user';

    return getRenderer(
      `Authentication ${status}from ${ipAddress} ${countryCode}for ${user}`
    )(value, record);
  },
  AuthenticationPrivilegeEscalation: (value, record) =>
    fallbackRenderer(
      value,
      record,
      fallbackFields.AuthenticationPrivilegeEscalation,
      'description'
    ),
  Canary: (value) => (typeof value === 'number' ? DateRenderer(value) : value),
  FailedRecord: (value) => value,
  Email: (value, record) => {
    const { email_sender, user_email } = record;
    const result: string[] = [];
    if (email_sender) {
      result.push('Sender: {email_sender}');
    }

    // Recipient
    if (user_email) {
      result.push('Recipient: {user_email}');
    }

    return result.length
      ? getRenderer(result.join(', '))(value, record)
      : 'None';
  },
  Endpoint: (value, record) =>
    fallbackRenderer(value, record, fallbackFields.Endpoint, 'description'),
  EndpointModuleLoad: (value, record) =>
    fallbackRenderer(
      value,
      record,
      fallbackFields.EndpointModuleLoad,
      'description'
    ),
  EndpointProcess: (value, record) =>
    fallbackRenderer(
      value,
      record,
      fallbackFields.EndpointProcess,
      'description'
    ),
  Network: (value, record) =>
    fallbackRenderer(
      value,
      record,
      fallbackFields.Network,
      'description',
      false
    ),
  NetworkDHCP: (value, record) =>
    getRenderer('MAC Address {device_mac} assigned IP {device_ip}', 'unknown')(
      value,
      record
    ),
  NetworkDNS: (value, record) => getRenderer('{dns_query}')(value, record),
  NetworkFlow: (value, record) =>
    fallbackRenderer(
      value,
      record,
      fallbackFields.NetworkFlow,
      undefined,
      false
    ),
  NetworkHTTP: networkRenderer,
  NetworkProxy: networkRenderer,
  Notification: (value, record) =>
    fallbackRenderer(value, record, fallbackFields.Notification, 'description'),
  NotificationVulnerability: (value, record) =>
    fallbackRenderer(
      value,
      record,
      fallbackFields.NotificationVulnerability,
      'description'
    ),
};
